import { useState, useCallback } from 'react';
import CouponService from '../services/CouponService/CouponService';
import { alertService } from '../services/AlertService';
import { handleError } from '../model/handleError';

const extractHttpError = (error) => {
  let status = null;
  let message = null;

  if (error?.response) {
    status = error.response.status;
    const data = error.response.data;
    message = (typeof data === 'string') ? data : (data?.error || data?.message || null);
  } else if (typeof error === 'object' && error && 'status' in error) {
    status = error.status;
    message = error?.data?.error || error?.data?.message || error?.statusText || null;
  }
  if (!message && error?.message) message = error.message;

  return { status, message };
};

const useCoupon = () => {
  const [isLoading, setIsLoading] = useState(false);

  const getCouponCampaigns = useCallback(async (id_classi_superpartner) => {
    setIsLoading(true);
    try {
      return await CouponService.getCampaigns(id_classi_superpartner);
    } catch (error) {
      handleError(error);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, []);

  const createCouponCampaign = useCallback(async (data) => {
    setIsLoading(true);
    try {
      const res = await CouponService.createCampaign(data);
      const payload = (res && typeof res === 'object' && 'data' in res) ? res.data : res;

      if (payload?.error) {
        alertService.error(payload.error);
        return false;
      }

      alertService.success('Campagna coupon creata con successo!', { autoClose: true });
      return true;
    } catch (error) {
      const { status, message } = extractHttpError(error);

      if (status === 422) {
        alertService.error(message || 'Errore di validazione dei campi.');
      } else if (status === 409) {
        alertService.error(message || 'Conflitto: periodo non disponibile o codice già esistente.');
      } else if (status === 500) {
        alertService.error('Errore interno del server. Riprova più tardi.');
      } else if (status) {
        alertService.error(message || 'Operazione non riuscita.');
      } else {
        alertService.error('Errore di rete. Controlla la connessione e riprova.');
        handleError(error);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteCouponCampaign = useCallback(async (id_promozione) => {
    setIsLoading(true);
    try {
      const res = await CouponService.deleteCampaign(id_promozione);
      const payload = (res && typeof res === 'object' && 'data' in res) ? res.data : res;

      if (payload?.error) {
        alertService.error(payload.error);
        return false;
      }

      alertService.success('Campagna eliminata con successo.', { autoClose: true });
      return true;
    } catch (error) {
      const { status, message } = extractHttpError(error);

      if (status === 404) {
        alertService.error(message || 'Campagna non trovata o già eliminata.');
      } else if (status) {
        alertService.error(message || 'Operazione non riuscita.');
      } else {
        alertService.error('Errore di rete. Controlla la connessione e riprova.');
        handleError(error);
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // --- NUOVE FUNZIONI INTEGRATE ---

  const validateCoupon = useCallback(async (codice_coupon, id_classi_superpartner) => {
    setIsLoading(true);
    try {
      return await CouponService.validateCoupon(codice_coupon, id_classi_superpartner);
    } catch (error) {
      const { status, message } = extractHttpError(error);
      if (status === 404) {
        alertService.error(message || 'Il codice coupon inserito non è valido o è scaduto.');
      } else if (status === 409) {
        alertService.error(message || 'Questo coupon ha esaurito gli utilizzi disponibili.');
      } else {
        alertService.error(message || 'Impossibile validare il coupon.');
      }
      throw error; // Rilanciamo l'errore per permettere al componente di reagire
    } finally {
      setIsLoading(false);
    }
  }, []);

  const recordCouponUsage = useCallback(async (data) => {
    setIsLoading(true);
    try {
      await CouponService.recordCouponUsage(data);
      // Operazione di background, nessun alert di successo necessario
      return true;
    } catch (error) {
      // In caso di errore, logghiamo per il debug ma non blocchiamo l'utente
      console.error("Errore nella registrazione dell'utilizzo del coupon:", error);
      handleError(error);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const checkActiveCampaign = useCallback(async (data) => {
    setIsLoading(true);
    try {
      return await CouponService.checkActiveCampaign(data);
    } catch (error) {
      handleError(error);
      return { active: false }; // In caso di errore, assumiamo non ci siano coupon disponibili
    } finally {
      setIsLoading(false);
    }
  }, []);

  // --- FINE NUOVE FUNZIONI INTEGRATE ---

  return { 
    isLoading, 
    getCouponCampaigns, 
    createCouponCampaign, 
    deleteCouponCampaign,
    // Aggiungiamo le nuove funzioni all'export
    validateCoupon,
    recordCouponUsage,
    checkActiveCampaign
  };
};

export default useCoupon;

